import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Header from '../components/Header'
import Footer from '../components/Footer'
import Contact from '../components/Contact'

const WebDesignerCenturionPage = () => {
  const localSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://rdsstudio.co.za/web-designer-centurion#webpage",
    "url": "https://rdsstudio.co.za/web-designer-centurion",
    "name": "Web Designer Centurion | RDS Studio — Professional Website Design",
    "description": "RDS Studio — Centurion's local web design agency. Custom WordPress websites, SEO & Google Ads. Web designer near you in Centurion, Pretoria & Gauteng.",
    "inLanguage": "en-ZA",
    "isPartOf": { "@id": "https://rdsstudio.co.za/#website" },
    "about": { "@id": "https://rdsstudio.co.za/#localbusiness" },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://rdsstudio.co.za/" },
        { "@type": "ListItem", "position": 2, "name": "Web Designer Centurion", "item": "https://rdsstudio.co.za/web-designer-centurion" }
      ]
    }
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who is the best web designer near me in Centurion?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "RDS Studio is a top-rated web design agency based in Centurion, Gauteng. We build custom WordPress websites, e-commerce stores, and digital marketing solutions for businesses in Centurion, Pretoria, Irene, Lyttelton, The Reeds, Highveld, Rooihuiskraal, Clubview, Hennopspark and across Gauteng."
        }
      },
      {
        "@type": "Question",
        "name": "How much does a website cost in Centurion?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Website design in Centurion starts from R8,500 for a basic 5-page WordPress business website. E-commerce stores start from R18,500. Custom web applications start from R35,000. All websites include mobile-first design, on-page SEO and hosting setup. Contact RDS Studio for a free, itemised quote."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer web design services in Pretoria as well?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. RDS Studio is based in Centurion and regularly serves clients across Pretoria, Midrand, Irene, Lyttelton, Highveld, The Reeds, Rooihuiskraal North, Clubview, Amberfield Glen, Hennopspark, Eldoraigne and the wider Gauteng area. We offer in-person meetings for local clients."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to build a website in Centurion?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A standard 5-page WordPress business website takes 2–3 weeks. A custom website with booking, e-commerce or integrations takes 4–6 weeks. We give you a clear timeline upfront before we start."
        }
      },
      {
        "@type": "Question",
        "name": "Do you do SEO and Google Ads for Centurion businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. RDS Studio offers SEO packages from R4,500/month including keyword research, on-page optimisation, Google Business Profile management, and monthly ranking reports. We also manage Google Ads campaigns for local Centurion and Pretoria businesses."
        }
      }
    ]
  }

  const suburbs = [
    'Irene', 'Lyttelton', 'Highveld', 'The Reeds', 'Rooihuiskraal',
    'Clubview', 'Die Hoewes', 'Wierdaglen', 'Amberfield Glen', 'Eldoraigne',
    'Hennopspark', 'Erasmia', 'Pretoria', 'Midrand', 'Faerie Glen',
    'Mooikloof', 'Garsfontein', 'Silverton', 'Waterkloof', 'Montana'
  ]

  const services = [
    { icon: '🖥️', name: 'WordPress Website Design', desc: 'Custom-built WordPress websites that look great, load fast and rank on Google.' },
    { icon: '🛒', name: 'E-commerce Stores', desc: 'WooCommerce online stores built to convert visitors into paying customers.' },
    { icon: '🔍', name: 'SEO Centurion', desc: 'Get found on Google when customers search "web designer near me" or your service.' },
    { icon: '📣', name: 'Google Ads Management', desc: 'Targeted Google Ads campaigns that bring qualified local leads to your business.' },
    { icon: '📱', name: 'Social Media Marketing', desc: 'Facebook, Instagram & LinkedIn management with content that drives engagement.' },
    { icon: '🤖', name: 'AI Chatbots & Automation', desc: 'Intelligent chatbots that answer enquiries 24/7 and automate your marketing.' },
  ]

  return (
    <div>
      <Helmet>
        <title>Web Designer Centurion | RDS Studio — WordPress, SEO & Digital Marketing</title>
        <meta name="description" content="Looking for a web designer near you in Centurion? RDS Studio builds custom WordPress websites, handles SEO & Google Ads. Serving Centurion, Pretoria & Gauteng. Free quote." />
        <link rel="canonical" href="https://rdsstudio.co.za/web-designer-centurion" />
        <link rel="alternate" hrefLang="en-ZA" href="https://rdsstudio.co.za/web-designer-centurion" />
        <meta property="og:title" content="Web Designer Centurion | RDS Studio" />
        <meta property="og:description" content="Centurion's local web design agency. Custom WordPress websites, SEO & Google Ads near you." />
        <meta property="og:url" content="https://rdsstudio.co.za/web-designer-centurion" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(localSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main id="main-content" className="main-content">

        {/* Hero */}
        <section className="section hero" style={{paddingTop:'6rem'}}>
          <div className="section-inner">
            <h1 className="title title-h1">Web Designer<br />Centurion</h1>
            <h2 className="subtitle subtitle-h2">Your Local Web Design Agency — Near You in Centurion, Pretoria & Gauteng</h2>
            <p className="description" style={{maxWidth:'680px',margin:'1.5rem auto 2rem'}}>
              RDS Studio is a Centurion-based web design agency delivering custom WordPress websites,
              SEO, Google Ads and marketing automation. We're the web designer near you —
              serving businesses in Centurion, Pretoria, Irene, Lyttelton, The Reeds and across Gauteng.
            </p>
            <div className="hero-buttons">
              <a href="#contact" className="btn btn-primary">Get a Free Quote</a>
              <Link to="/portfolio" className="btn btn-secondary">View Our Work</Link>
            </div>
          </div>
        </section>

        {/* Why Local Matters */}
        <section className="section">
          <div className="section-inner">
            <h2 className="title title-h2">Why Choose a Local Web Designer in Centurion?</h2>
            <p className="description" style={{maxWidth:'700px',margin:'0 auto 2.5rem'}}>
              Working with a web designer near you in Centurion means face-to-face meetings,
              same-timezone support and someone who understands the local market.
            </p>
            <div className="services-grid">
              <div className="service-card">
                <h3>📍 Local Knowledge</h3>
                <p>We know Centurion's business landscape — from Highveld Techno Park to Lyttelton and The Reeds. We build websites that speak to your local audience.</p>
              </div>
              <div className="service-card">
                <h3>🤝 In-Person Meetings</h3>
                <p>Based in Centurion, we're available for face-to-face consultations. No overseas teams, no language barriers — just direct communication.</p>
              </div>
              <div className="service-card">
                <h3>⚡ Fast Response</h3>
                <p>Same-timezone support means we're available when you need us. Most queries answered within 2 hours during business hours.</p>
              </div>
              <div className="service-card">
                <h3>📈 Built to Rank</h3>
                <p>Every website we build includes on-page SEO, Google-friendly structure and fast load speeds — so you show up when customers search for you nearby.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="section">
          <div className="section-inner">
            <h2 className="title title-h2">Web Design Services in Centurion</h2>
            <div className="services-grid">
              {services.map((s) => (
                <div key={s.name} className="service-card">
                  <h3>{s.icon} {s.name}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
            <div style={{textAlign:'center',marginTop:'2.5rem'}}>
              <Link to="/services" className="btn btn-primary">See All Services & Pricing</Link>
            </div>
          </div>
        </section>

        {/* Service Area */}
        <section className="section">
          <div className="section-inner">
            <h2 className="title title-h2">Areas We Serve Near Centurion</h2>
            <p className="description" style={{maxWidth:'680px',margin:'0 auto 2rem'}}>
              Our web design studio serves businesses across Centurion and the surrounding area.
              Wherever you are in Gauteng, we're the web designer near you.
            </p>
            <div style={{display:'flex',flexWrap:'wrap',gap:'0.75rem',justifyContent:'center',maxWidth:'800px',margin:'0 auto'}}>
              {suburbs.map((suburb) => (
                <span
                  key={suburb}
                  style={{
                    background:'rgba(45,212,191,0.1)',
                    border:'1px solid rgba(45,212,191,0.3)',
                    borderRadius:'2rem',
                    padding:'0.4rem 1rem',
                    fontSize:'0.9rem',
                    color:'var(--color-accent)'
                  }}
                >
                  {suburb}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section">
          <div className="section-inner">
            <h2 className="title title-h2">Frequently Asked Questions</h2>
            <div style={{maxWidth:'700px',margin:'0 auto'}}>
              {faqSchema.mainEntity.map((faq, i) => (
                <details
                  key={i}
                  style={{
                    marginBottom:'1rem',
                    background:'rgba(255,255,255,0.03)',
                    border:'1px solid rgba(255,255,255,0.08)',
                    borderRadius:'0.5rem',
                    padding:'1rem 1.25rem'
                  }}
                  itemScope
                  itemType="https://schema.org/Question"
                >
                  <summary style={{cursor:'pointer',fontWeight:600,fontSize:'1rem'}} itemProp="name">
                    {faq.name}
                  </summary>
                  <div
                    style={{marginTop:'0.75rem',color:'rgba(255,255,255,0.75)',lineHeight:1.7}}
                    itemScope
                    itemType="https://schema.org/Answer"
                    itemProp="acceptedAnswer"
                  >
                    <span itemProp="text">{faq.acceptedAnswer.text}</span>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA + Contact form */}
        <Contact />
      </main>

      <Footer />
    </div>
  )
}

export default WebDesignerCenturionPage
